import { isAgentHomeProjectBoundAtom } from '@/atoms/sideNavAtoms';
import { currentWorkHomeViewAtom } from '@heidi/agents/data';
import { useIsMobile, useMediaQuery } from '@heidi/utils';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isAgentHomeLandingAtom,
  shouldShowAgentHomePanel,
  useAgentHomePanel,
} from '../agent-home-panel-state';
import { useRecentItemsSource } from '../recents/use-recent-items-source';

/**
 * Grace window that lets the pointer travel from the Home row to the
 * preview's content (and back) without the preview closing mid-transfer.
 * Applies on both sides — trigger → content and content → trigger.
 */
export const HOME_HOVER_PREVIEW_CLOSE_GRACE_MS = 150;

export interface UseHomeHoverPreviewArgs {
  /** Same base-chat href the Home trigger itself uses, so the suppression
   *  check below reads the identical "is the persistent panel visible"
   *  signal `AgentHomePanel` computes. */
  baseChatHref: string;
}

export interface HomeHoverPreviewState {
  /** Whether the preview should currently render open. Always `false` when
   *  suppressed (mobile/touch-only layouts, or the persistent Home panel is
   *  already showing this same content), while Recents availability is
   *  unknown, or once both sources are confirmed empty. */
  isOpen: boolean;
  /** Record pointer-hover intent and probe Recents. Opens immediately from a
   *  warm cache with items, or after a cold probe resolves to items. */
  openViaHover: () => void;
  /** Schedule a close after the transfer-grace window; cancelled by a
   *  subsequent `openViaHover` within that window. */
  scheduleClose: () => void;
  /** Cancel any pending scheduled close without otherwise changing open
   *  state — used when the pointer/focus has landed on the *other* half of
   *  the trigger/content pair (e.g. focus moving from the trigger into the
   *  popover content, which fires the trigger's own `focusout` first). */
  keepOpen: () => void;
  /** Close now, cancelling any pending scheduled close. */
  closeImmediately: () => void;
  /** Wire straight into `Popover`'s `onOpenChange`. Open requests are ignored
   *  because the preview is hover-only; close requests dismiss immediately. */
  handleOpenChange: (next: boolean) => void;
  /** Prevent Radix from moving focus into hover-opened content. */
  handleContentOpenAutoFocus: (event: Event) => void;
}

/**
 * Hover-intent and pre-render availability state machine for the Home
 * preview. Kept separate from the Popover markup so `HomeNavItem` only wires
 * DOM listeners onto the trigger ref and passes the resulting handlers
 * through. The shared source reads cached items even while disabled; requests
 * run only while intent is present, and this primary-rail observer never owns
 * the persistent tray's 30s visits poll.
 */
export function useHomeHoverPreview({
  baseChatHref,
}: UseHomeHoverPreviewArgs): HomeHoverPreviewState {
  const router = useRouter();
  const isMobile = useIsMobile();
  const canHover = useMediaQuery('(hover: hover)', true);
  const { isOpen: isPanelOpenPreference } = useAgentHomePanel();
  const isHomeLanding = useAtomValue(isAgentHomeLandingAtom);
  const isProjectBound = useAtomValue(isAgentHomeProjectBoundAtom);
  const isRoutinesView = useAtomValue(currentWorkHomeViewAtom) === 'skills';

  const isPanelVisible = shouldShowAgentHomePanel({
    asPath: router.asPath,
    baseChatHref,
    isOpen: isPanelOpenPreference,
    isMobile,
    isHomeLanding,
    isProjectBound,
    isRoutinesView,
  });

  // Touch-only layouts have no hover intent to preview, and the persistent
  // Home panel already shows this exact Recents collection — showing both
  // at once would compete for the same content on the same screen.
  const canShowPreview = !isMobile && canHover && !isPanelVisible;

  const [hasIntent, setHasIntent] = useState(false);
  const { availability } = useRecentItemsSource({
    visitsEnabled: canShowPreview && hasIntent,
    chatsEnabled: canShowPreview && hasIntent,
    pollVisits: false,
  });
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current === null) return;
    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = null;
  }, []);

  useEffect(() => clearCloseTimeout, [clearCloseTimeout]);

  const openViaHover = useCallback(() => {
    if (!canShowPreview) return;
    clearCloseTimeout();
    setHasIntent(true);
  }, [canShowPreview, clearCloseTimeout]);

  const closeImmediately = useCallback(() => {
    clearCloseTimeout();
    setHasIntent(false);
  }, [clearCloseTimeout]);

  const keepOpen = useCallback(() => {
    if (!canShowPreview) return;
    clearCloseTimeout();
  }, [canShowPreview, clearCloseTimeout]);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHasIntent(false);
    }, HOME_HOVER_PREVIEW_CLOSE_GRACE_MS);
  }, [clearCloseTimeout]);

  // A completed (or in-flight) navigation always closes the preview — this
  // covers recent-row routing and visit-fragment links without each one
  // needing to know the preview exists.
  useEffect(() => {
    if (!hasIntent || !router.events) return;
    router.events.on('routeChangeStart', closeImmediately);
    return () => {
      router.events.off('routeChangeStart', closeImmediately);
    };
  }, [hasIntent, router.events, closeImmediately]);

  // Suppression can flip true (e.g. the persistent panel opens, or the
  // layout crosses the mobile breakpoint) while the preview is open.
  useEffect(() => {
    if (!canShowPreview && hasIntent) closeImmediately();
  }, [canShowPreview, hasIntent, closeImmediately]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) closeImmediately();
    },
    [closeImmediately]
  );

  const handleContentOpenAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  return {
    isOpen: canShowPreview && hasIntent && availability === 'has-items',
    openViaHover,
    scheduleClose,
    keepOpen,
    closeImmediately,
    handleOpenChange,
    handleContentOpenAutoFocus,
  };
}
